import React, { FunctionComponent, useMemo, useState, useCallback, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { Heading, Text, Icon, useDate, Skeleton, Grid, Button, useIsCashTheme } from '@afterpaytouch/core'
import { useTranslation } from 'next-i18next'
import clsx from 'clsx'
import { useGetOrderTransactionQuery } from '../../state/orders/hooks'
import { OrderPaymentSchedule } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import { Money } from '../Money'
import { getLineItems, getTextColor, hasLineItems as checkLineItems, isInstallmentWaived, getBorderClass } from './utils'
import { useGetInitialSchedules } from './hooks'
import { useConsumerSizes } from '../../utils/responsive'
import {
  getFirstDueSchedule,
  isScheduleOverdue as checkScheduleOverdue,
  isSchedulePartialRefund as checkSchedulePartialRefund,
  isSchedulePaid as checkSchedulePaid,
  isSchedulePayable as checkSchedulePayable,
} from '../../state/orders/utils'
import { OrderScheduleInstallmentLine } from './OrderScheduleInstallmentLine'
import { OrderScheduleIcon } from './OrderScheduleIcon'
import { OrderScheduleInstallmentLabel } from './OrderScheduleInstallmentLabel'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'orders']

interface OrderScheduleProps {
  orderId: string
}

interface OrderScheduleInstallmentProps {
  orderId: string
  installment: OrderPaymentSchedule
  numberOfInstallments: number
  isNextDue: boolean
  isFirstInstallment: boolean
  isCollapsed: boolean
  onHandleToggle: () => void
}

export const OrderScheduleTimeLine: FunctionComponent<OrderScheduleProps> = ({ orderId }) => {
  const testNameSpace = 'order-schedule'
  const [isCollapsed, setIsCollapsed] = useState(true)
  const { data, isLoading } = useGetOrderTransactionQuery(orderId)
  const { paymentScheduleSet } = data ?? {}
  const { t } = useTranslation(I18N_NAMESPACE)
  // @ts-ignore: OPERATION BLEED STOPPER
  const firstDueSchedule = useMemo(() => getFirstDueSchedule(paymentScheduleSet), [paymentScheduleSet])
  const handleCollapse = (): void => setIsCollapsed(!isCollapsed)
  const initialSchedules = useGetInitialSchedules(orderId)

  // @ts-ignore: OPERATION BLEED STOPPER
  const hasMoreThan4Schedule = paymentScheduleSet?.length > 4
  const schedules = isCollapsed && hasMoreThan4Schedule ? initialSchedules : paymentScheduleSet
  const headingStyle = hasMoreThan4Schedule ? 'mb-6' : 'invisible md:visible md:mb-6'
  return (
    <>
      <div className={headingStyle} data-testid={testNameSpace}>
        <Heading size={'L'}>{t('orders:orderSchedule:schedule')}</Heading>
      </div>
      {isLoading ? (
        <div className='pt-8'>
          <Skeleton />
        </div>
      ) : (
        <>
          <div>
            {/* @ts-ignore: OPERATION BLEED STOPPER */}
            {schedules.map((installment, i) => (
              <OrderScheduleInstallment
                key={`installment:${installment.id}`}
                installment={installment}
                orderId={orderId}
                // @ts-ignore: OPERATION BLEED STOPPER
                numberOfInstallments={paymentScheduleSet.length}
                isNextDue={firstDueSchedule?.id === installment?.id}
                isFirstInstallment={i === 0}
                isCollapsed={!hasMoreThan4Schedule ? false : isCollapsed}
                onHandleToggle={handleCollapse}
              />
            ))}
          </div>
          {!isCollapsed && hasMoreThan4Schedule && (
            <div className='mt-6'>
              <Button.Ghost onClick={handleCollapse} padding='Fluid' size='Small' testNameSpace={`${testNameSpace}-toggle`}>
                {t('orders:orderSchedule:toggleSchedule:hide')}
              </Button.Ghost>
            </div>
          )}
        </>
      )}
    </>
  )
}

export const OrderScheduleInstallment: FunctionComponent<OrderScheduleInstallmentProps> = ({
  orderId,
  installment,
  numberOfInstallments,
  isNextDue,
  isFirstInstallment,
  isCollapsed,
  onHandleToggle,
}) => {
  const testNameSpace = 'order-schedule-item'
  const { textSize } = useConsumerSizes()
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const { data } = useGetOrderTransactionQuery(orderId)
  const {
    // @ts-ignore: OPERATION BLEED STOPPER
    orderSummary: { donation },
  } = data ?? {}
  const { formatDate } = useDate({ locale, weekday: 'short' })
  const { t } = useTranslation(I18N_NAMESPACE)
  const isCashTheme = useIsCashTheme()
  const [dropDownActive, setDropDownActive] = useState<boolean>(false)
  const isSchedulePaid = useMemo(() => checkSchedulePaid(installment), [installment])
  const isScheduleOverdue = useMemo(() => checkScheduleOverdue(installment), [installment])
  const isSchedulePartialRefund = useMemo(() => checkSchedulePartialRefund(installment), [installment])
  const lineItems = useMemo(() => getLineItems({ installment, donation, isFirstInstallment }), [donation, installment, isFirstInstallment])
  const hasLineItems = useMemo(() => checkLineItems(lineItems), [lineItems])
  const textColor = useCallback(getTextColor, [isScheduleOverdue, isSchedulePaid, isNextDue, isSchedulePartialRefund, isCashTheme])(
    isScheduleOverdue,
    isSchedulePaid,
    isNextDue,
    isSchedulePartialRefund,
    isCashTheme
  )
  const showToggleButton = isFirstInstallment && isCollapsed
  const isWaived = isInstallmentWaived(installment)
  const isSchedulePayable = useMemo(() => checkSchedulePayable(installment), [installment])
  const borderClass = getBorderClass(isCashTheme, isSchedulePaid, isSchedulePayable)

  useEffect(() => {
    if (isCollapsed) {
      setDropDownActive(false)
    }
  }, [isCollapsed])

  return (
    <div className='group' data-testid={testNameSpace}>
      <Grid gutter={{ sm: 0.5 }}>
        <Grid.Item sm='auto'>
          <div className='relative flex h-full w-6 justify-center pt-1'>
            <div
              className={clsx(borderClass, isCashTheme ? 'bottom-0 top-5' : '-bottom-1 top-1', 'absolute border-0 border-l-2 border-solid group-last:hidden')}
            ></div>
            <div className='relative'>
              <OrderScheduleIcon installment={installment} isNextDue={isNextDue} />
            </div>
          </div>
        </Grid.Item>
        <Grid.Item>
          <OrderScheduleCaret
            show={hasLineItems && !isCollapsed && !isWaived}
            isOpen={dropDownActive}
            handleToggle={() => setDropDownActive(!dropDownActive)}
            testNameSpace={testNameSpace}
          >
            <div className='flex flex-col pb-6 group-last:pb-0'>
              <Text renderAs='span' size={textSize} bold color={textColor} testNameSpace={`${testNameSpace}-heading`}>
                {isNextDue && `${t('orders:orderSchedule:nextDue')} `}
                {formatDate(installment.installmentDueDate.toString())}
              </Text>
              <OrderScheduleInstallmentLabel orderId={orderId} installment={installment} numberOfInstallments={numberOfInstallments} isNextDue={isNextDue} />
            </div>
            <Grid.Item sm='auto'>
              <Grid>
                <Money size='S' bold color={textColor} value={installment.amountSummaryDisplayValue} testNameSpace={`${testNameSpace}-payment-amount`} />
              </Grid>
            </Grid.Item>
          </OrderScheduleCaret>
          {showToggleButton && (
            <div className={clsx(!dropDownActive && 'mb-6')}>
              <Button.Ghost onClick={onHandleToggle} padding='Fluid' size='Small' testNameSpace={`${testNameSpace}-toggle`}>
                {t('orders:orderSchedule:toggleSchedule:show')}
              </Button.Ghost>
            </div>
          )}
          {dropDownActive && (
            <div
              className='b-4 -mt-2 mr-5 grid grid-cols-[auto,auto,auto] items-center gap-x-2 gap-y-4 pb-8 group-last:pb-2 group-last:pt-6'
              data-testid={`${testNameSpace}-line`}
            >
              {lineItems.map(({ type, item }) => (
                <OrderScheduleInstallmentLine key={`${String(item.createdDate)} ${String(item.displayName)}`} type={type} item={item} />
              ))}
            </div>
          )}
        </Grid.Item>
      </Grid>
    </div>
  )
}

interface OrderScheduleCaretProps {
  show: boolean
  isOpen: boolean
  handleToggle: () => void
  testNameSpace: string
  children: ReactNode
}

export const OrderScheduleCaret: FunctionComponent<OrderScheduleCaretProps> = ({ show, isOpen, handleToggle, testNameSpace, children }) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  return show ? (
    <div className='hover:cursor-pointer' onClick={() => handleToggle()} data-testid={`${testNameSpace}-caret`}>
      <Grid>
        {children}
        <Grid.FixedItem width={1}>
          <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size='Mini' title={t(`orders:orderSchedule:caretLabels:${isOpen ? 'hide' : 'display'}`)} role='img' />
        </Grid.FixedItem>
      </Grid>
    </div>
  ) : (
    <div data-testid='caret' className='pr-4'>
      <Grid>{children}</Grid>
    </div>
  )
}
